import {
  addToast,
  Button,
  Chip,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { t } from "@lingui/core/macro";
import { Plural, Trans } from "@lingui/react/macro";
import * as Sentry from "@sentry/react";
import { ExternalLink } from "lucide-react";
import { useState } from "react";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeLoader } from "@/Components/NextBase/LimeLoader";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { api } from "@/lib/api-client";
import { formatInvoicePeriodDate } from "@/pages/Dashboard/CustomerSubscriptionOverview/components/subscriptionInvoiceUtils";
import { TaxRegisterAuthorizationDialog } from "@/pages/Dashboard/TaxRegister/AuthorizationDialog";
import { InvoiceDetailsModal } from "@/pages/Dashboard/TaxRegister/Invoices/InvoiceDetailsModal";

export function CompanySubscriptionsModal({
  companyId,
  onClose,
}: {
  companyId: number | null;
  onClose: () => void;
}) {
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null,
  );

  const { data, isLoading, refetch } =
    api.company.useGetCompanyFundedSubscriptions(companyId);

  const { data: posAuthData, isPending: isPosAuthPending } =
    api.cookie.useGetPosToken();
  const isPosAuthenticated = posAuthData?.userId != null;

  const {
    mutateAsync: issueEstimate,
    isPending,
    processedErrorMessage,
    reset: resetIssueEstimate,
  } = api.customer.useIssueCustomerSubscriptionEstimates();

  const billableShares =
    data?.outstandingShares.filter((share) => !share.isInvoiced) ?? [];

  function handleClose() {
    setSelectedDocumentId(null);
    resetIssueEstimate();
    onClose();
  }

  async function handleIssue() {
    if (companyId == null || billableShares.length === 0) return;
    try {
      // Only the shares on screen: one that became billable while this was open must not be billed.
      const result = await issueEstimate({
        body: {
          shareIds: billableShares.map((share) => share.id),
          sendEstimateOnEmail: false,
        },
      });
      const billedShareCount = result.documents.flatMap(
        (document) => document.shareIds,
      ).length;
      addToast({
        title: t`Predračun je bil izdan`,
        description: t`Obračunanih obdobij: ${billedShareCount}`,
        color: "success",
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  return (
    <>
      <LimeModal
        isOpen={companyId != null}
        onClose={handleClose}
        size="3xl"
        scrollBehavior="inside"
        data-identifier="company-subscriptions-modal"
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <Trans>Sofinancirani paketi</Trans>
                {data?.company.companyName && (
                  <span className="font-normal text-default-500 text-small">
                    {data.company.companyName}
                  </span>
                )}
              </ModalHeader>
              <ModalBody>
                {isLoading || !data ? (
                  <LimeLoader />
                ) : (
                  <div className="flex flex-col gap-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <Chip
                        size="sm"
                        radius="sm"
                        variant="flat"
                        color={
                          data.outstandingCents > 0 ? "warning" : "success"
                        }
                        data-identifier="company-outstanding-total"
                      >
                        {data.outstandingCents > 0
                          ? t`Neporavnano ${data.outstandingFormatted}`
                          : t`Vse poravnano`}
                      </Chip>
                      {data.billableCents > 0 && (
                        <Chip
                          size="sm"
                          radius="sm"
                          variant="flat"
                          data-identifier="company-billable-total"
                        >
                          {t`Za obračun ${data.billableFormatted}`}
                        </Chip>
                      )}
                    </div>

                    <Section title={t`Paketi`}>
                      {data.fundedSubscriptions.length === 0 ? (
                        <Empty>
                          <Trans>
                            To podjetje ne sofinancira nobenega paketa
                          </Trans>
                        </Empty>
                      ) : (
                        data.fundedSubscriptions.map((subscription) => (
                          <Row
                            key={subscription.id}
                            left={
                              <span className="flex items-center gap-2">
                                {subscription.customer.name}
                                {subscription.status === "canceled" && (
                                  <Chip size="sm" radius="sm" variant="flat">
                                    {t`Preklicano`}
                                  </Chip>
                                )}
                              </span>
                            }
                            right={
                              subscription.companyShareType === "percentage"
                                ? `${subscription.companyShareValue}%`
                                : t`fiksno`
                            }
                          />
                        ))
                      )}
                    </Section>

                    <Section title={t`Neobračunana obdobja`}>
                      {billableShares.length === 0 ? (
                        <Empty>
                          <Trans>Ni obdobij za obračun</Trans>
                        </Empty>
                      ) : (
                        billableShares.map((share) => (
                          <Row
                            key={share.id}
                            left={`${share.customer.name} · ${formatInvoicePeriodDate(share.periodStart)}`}
                            right={share.amountFormatted}
                          />
                        ))
                      )}
                    </Section>

                    {data.documents.length > 0 && (
                      <Section
                        title={
                          data.documents.length === data.documentLimit
                            ? t`Računi (zadnjih ${data.documentLimit})`
                            : t`Računi`
                        }
                      >
                        {data.documents.map((document) => (
                          <Row
                            key={document.spaceInvoicesDocumentId}
                            left={
                              <span className="flex items-center gap-2">
                                <span
                                  className={`font-mono ${document.documentCanceled ? "text-danger line-through" : ""}`}
                                >
                                  {document.documentNumber ?? t`Brez številke`}
                                </span>
                                <span className="text-default-400 text-tiny">
                                  <Plural
                                    value={document.periodCount}
                                    one="# paket"
                                    two="# paketa"
                                    few="# paketi"
                                    other="# paketov"
                                  />
                                </span>
                              </span>
                            }
                            right={
                              <span className="flex items-center gap-2">
                                <span
                                  className={`text-tiny ${document.isPaid ? "text-success" : "text-warning"}`}
                                >
                                  {document.isPaid
                                    ? t`Plačano`
                                    : t`Čaka na plačilo`}
                                </span>
                                <span>{document.totalFormatted}</span>
                                <Button
                                  size="sm"
                                  isIconOnly
                                  variant="light"
                                  onPress={() =>
                                    setSelectedDocumentId(
                                      document.spaceInvoicesDocumentId,
                                    )
                                  }
                                  data-identifier="open-company-document-button"
                                >
                                  <ExternalLink size={14} />
                                </Button>
                              </span>
                            }
                          />
                        ))}
                      </Section>
                    )}

                    <LimeAlert color="danger" message={processedErrorMessage} />
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={handleClose}>
                  <Trans>Zapri</Trans>
                </Button>
                <Button
                  color="primary"
                  onPress={handleIssue}
                  isLoading={isPending}
                  isDisabled={billableShares.length === 0}
                  startContent={<ExternalLink size={16} />}
                  data-identifier="issue-company-estimate-button"
                >
                  <Trans>Izdaj predračun za neobračunana obdobja</Trans>
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </LimeModal>

      <InvoiceDetailsModal
        isAuthenticated={isPosAuthenticated}
        invoiceId={selectedDocumentId || undefined}
        onClose={() => {
          setSelectedDocumentId(null);
          refetch();
        }}
        disableNavigationOnCancel
      />

      {/* Only on the way into a document, so browsing the list is not interrupted by a PIN prompt. */}
      {selectedDocumentId != null &&
        !isPosAuthenticated &&
        !isPosAuthPending && <TaxRegisterAuthorizationDialog />}
    </>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-semibold text-default-500 text-tiny uppercase">
        {title}
      </span>
      {children}
    </div>
  );
}

function Row({
  left,
  right,
}: {
  left: React.ReactNode;
  right: React.ReactNode;
}) {
  return (
    <div className="flex justify-between gap-2 text-default-600 text-small">
      <span>{left}</span>
      <span className="text-default-500">{right}</span>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <span className="text-default-400 text-tiny">{children}</span>;
}
